import React from 'react';
import { Box, Button, CircularProgress, Typography, Stack } from '@mui/material';
import { peopleService } from '../../services/api';

// Utility: get display name from various shapes
const getFullName = (p) => p?.FullName || p?.fullName || `${p?.FirstName || p?.firstName || ''} ${p?.LastName || p?.lastName || ''}`.trim();

const collator = new Intl.Collator('bg', { usage: 'sort', sensitivity: 'base' });

function firstLetter(name) {
  const s = (name || '').trim();
  if (!s) return '#';
  const ch = s[0].toUpperCase();
  // If not A-Z or А-Я, group under '#'
  const latin = ch >= 'A' && ch <= 'Z';
  const cyr = ch >= 'А' && ch <= 'Я';
  return (latin || cyr) ? ch : '#';
}

export default function VerticalAZCatalog({
  letter: letterProp,
  onLetterChange,
  selectedId,
  onSelect,
  collapsed = false,
  activeFilter: activeFilterProp = 'active',   // STAFF-S1 — Active is the default view
  onActiveFilterChange,
}) {

  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(30);
  const [total, setTotal] = React.useState(0);
  const [activeFilter, setActiveFilter] = React.useState(activeFilterProp || 'active');

  const [rows, setRows] = React.useState([]);
  const listRef = React.useRef(null);
  const scrollByLetter = React.useRef(new Map());

  const lettersLatin = React.useMemo(() => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''), []);
  const lettersCyr = React.useMemo(() => 'АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЬЮЯ'.split(''), []);
  const [alphabet, setAlphabet] = React.useState('latin'); // 'latin' | 'cyr'
  const allLetters = alphabet === 'latin' ? lettersLatin : lettersCyr;

  // Auto-detect default alphabet after first page load (once)
  const autoSetAlphabetRef = React.useRef(true);
  React.useEffect(() => {
    if (!autoSetAlphabetRef.current) return;
    if (!rows || rows.length === 0) return;
    let latin = 0, cyr = 0;
    for (const p of rows) {
      const L = firstLetter(getFullName(p));
      if (L >= 'A' && L <= 'Z') latin++; else if (L >= 'А' && L <= 'Я') cyr++;
    }
    const next = cyr > latin ? 'cyr' : 'latin';
    setAlphabet(next);
    autoSetAlphabetRef.current = false;
  }, [rows]);

  // Controlled letter: null = All (declare before effects that use it)
  const [letter, setLetter] = React.useState(letterProp ?? null);
  React.useEffect(() => { if (letterProp !== undefined && letterProp !== letter) setLetter(letterProp); }, [letterProp]);
  React.useEffect(() => { onLetterChange?.(letter); setPage(1); }, [letter]);

  // Load current page from server (paged API)
  React.useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await peopleService.listPaged({ page, pageSize, initial: letter || undefined, active: activeFilter });
        if (!active) return;
        const items = res?.items || [];
        setRows(items);
        setTotal(res?.total ?? items.length);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, pageSize, letter, activeFilter]);

  // Derived: sections map from current page only (server already sorts)
  const sectionsMap = React.useMemo(() => {
    const byLetter = new Map();
    for (const p of rows) {
      const name = getFullName(p) || '';
      const L = firstLetter(name);
      if (!byLetter.has(L)) byLetter.set(L, []);
      byLetter.get(L).push(p);
    }
    return byLetter;
  }, [rows]);


  // Restore scroll per letter when changing letter or alphabet
  React.useEffect(() => {
    const el = listRef.current; if (!el) return;
    const key = `${alphabet}:${letter ?? 'ALL'}`;
    const y = scrollByLetter.current.get(key) ?? 0;
    el.scrollTop = y;
  }, [letter, alphabet]);

  const handleScroll = () => {
    const el = listRef.current; if (!el) return;
    const key = `${alphabet}:${letter ?? 'ALL'}`;
    scrollByLetter.current.set(key, el.scrollTop);
  };

  const onClickLetter = (L) => {
    setLetter(prev => prev === L ? null : L);
  };

  // Sections to render: filtered by alphabet and letter
  const sectionsToRender = React.useMemo(() => {
    if (letter) {
      const items = sectionsMap.get(letter) || [];
      return [{ letter, items }];
    }
    // ALL: show letters present on current page only
    const arr = [];
    for (const [L, items] of sectionsMap.entries()) {
      arr.push({ letter: L, items });
    }
    // Sort letters for stability
    arr.sort((a,b)=> collator.compare(a.letter, b.letter));
    return arr;
  }, [letter, sectionsMap]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: collapsed ? '44px 0' : '44px minmax(0, 1fr)', gridTemplateRows: 'auto 1fr', gap: 1, alignItems: 'start', minHeight: 0, width: '100%', maxWidth:'100%', overflow:'hidden', transition: 'grid-template-columns 260ms ease' }}>
      {/* Vertical A–Z with alphabet toggle (left rail) */}
      <Box sx={{ bgcolor: 'background.paper', border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1.5, p: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, gridColumn: '1', gridRow: '1 / span 2' }}>
        <Stack direction="column" spacing={0.5} sx={{ mb: 0.5 }}>
          <Button size="small" onClick={() => setAlphabet('latin')} sx={{ minWidth: 0, px: 0.5, fontSize: 10, color: alphabet==='latin' ? 'primary.main' : 'text.secondary' }}>A-Z</Button>
          <Button size="small" onClick={() => setAlphabet('cyr')} sx={{ minWidth: 0, px: 0.5, fontSize: 10, color: alphabet==='cyr' ? 'primary.main' : 'text.secondary' }}>А-Я</Button>
        </Stack>
        {allLetters.map(L => (
          <Button key={L} size="small" onClick={() => onClickLetter(L)} sx={{ minWidth: 0, width: 28, height: 28, p: 0, border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1, fontSize: 12, color: letter===L ? 'primary.main' : 'text.primary', bgcolor: letter===L ? 'action.selected' : 'background.paper' }}>{L}</Button>
        ))}
        <Box sx={{ mt: 0.5 }}>
          <Button size="small" onClick={() => setLetter(null)} sx={{ minWidth: 0, px: 0.5, fontSize: 11 }}>All</Button>
        </Box>
      </Box>

      {/* Quick filter All/Active/Inactive (right header) */}
      <Box sx={{ bgcolor:'background.paper', border: theme => `1px solid ${theme.palette.divider}`, borderRadius:1.5, p:0.5, display:'flex', gap:0.5, alignItems:'center', justifyContent:'center', gridColumn: '2', gridRow: '1' }}>
        <Button size="small" variant={activeFilter==='all'?'contained':'text'} onClick={()=>{ setPage(1); setActiveFilter('all'); onActiveFilterChange?.('all'); }}>All</Button>
        <Button size="small" variant={activeFilter==='active'?'contained':'text'} onClick={()=>{ setPage(1); setActiveFilter('active'); onActiveFilterChange?.('active'); }}>Active</Button>
        <Button size="small" variant={activeFilter==='inactive'?'contained':'text'} onClick={()=>{ setPage(1); setActiveFilter('inactive'); onActiveFilterChange?.('inactive'); }}>Inactive</Button>
      </Box>

      {/* Catalog list (names only) + paginator (right column) */}
      <Box sx={{ display: collapsed ? 'none' : 'grid', gridTemplateRows: '1fr auto', minHeight: 0, minWidth:0, gridColumn: '2', gridRow: '2' }}>
        <Box ref={listRef} onScroll={handleScroll} sx={{ bgcolor: 'background.paper', border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1.5, overflow: 'auto', transition: 'opacity 200ms ease', opacity: collapsed ? 0 : 1, minWidth:0 }}>
          {loading ? (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>
          ) : sectionsToRender.length === 0 ? (
            <Box sx={{ p: 2, color: 'text.secondary' }}>No people</Box>
          ) : (
            sectionsToRender.map(sec => (
              <Box key={sec.letter}>
                <Box id={`sec-${sec.letter}`} sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', borderBottom: theme => `1px solid ${theme.palette.divider}`, px: 1.5, py: 0.75 }}>
                  <Typography variant="overline" sx={{ letterSpacing: '0.08em', opacity: 0.8 }}>{sec.letter}</Typography>
                </Box>
                {sec.items.length === 0 ? (
                  <Box sx={{ p: 1.5, color: 'text.secondary' }}>No people</Box>
                ) : sec.items.map(p => {
                  const id = p.Id ?? p.id; const name = getFullName(p) || '—';
                  const active = id === (selectedId ?? null);
                  return (
                    <Box key={id}
                         onClick={() => onSelect?.(p)}
                         role="button"
                         aria-selected={active ? 'true' : 'false'}
                         title={name}
                         sx={{ cursor:'pointer', px:1.25, py:0.75, minHeight:40, display:'flex', alignItems:'center', gap:1,
                               borderBottom: theme=>`1px solid ${theme.palette.divider}`,
                               borderLeft: theme=>`3px solid ${active ? theme.palette.primary.main : 'transparent'}`,
                               bgcolor: active ? 'action.selected' : 'transparent', color:'text.primary',
                               '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' } }}>
                      <Typography variant="body2" sx={{ fontWeight: active ? 600 : 400, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{name}</Typography>
                    </Box>
                  );
                })}
              </Box>
            ))
          )}
        </Box>
        {/* Pagination controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Prev</Button>
            <Typography variant="body2">{page}</Typography>
            <Button size="small" onClick={() => setPage(p => (p * pageSize < total ? p + 1 : p))} disabled={page * pageSize >= total}>Next</Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {[30,50,100].map(sz => (
              <Button key={sz} size="small" variant={pageSize===sz?'contained':'text'} onClick={()=>{ setPageSize(sz); setPage(1); }} sx={{ textTransform:'none' }}>{sz}</Button>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

